/**
 * SEO Utilities for Evans Oven
 */

/**
 * Generate a URL-friendly slug from a title
 * @example generateSlug("Garlic Butter Pizza") => "garlic-butter-pizza"
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Convert time string to ISO 8601 duration format for schema
 * @example parseTimeToISO("30 min") => "PT30M"
 * @example parseTimeToISO("1 hour 30 min") => "PT1H30M"
 */
export function parseTimeToISO(timeStr?: string): string | undefined {
  if (!timeStr) return undefined;

  const lower = timeStr.toLowerCase();
  let hours = 0;
  let minutes = 0;

  // Match hours
  const hourMatch = lower.match(/(\d+)\s*(?:hour|hr|h)/);
  if (hourMatch) {
    hours = parseInt(hourMatch[1], 10);
  }

  // Match minutes
  const minMatch = lower.match(/(\d+)\s*(?:minute|min|m(?!onth))/);
  if (minMatch) {
    minutes = parseInt(minMatch[1], 10);
  }

  // If only a number, assume minutes
  if (hours === 0 && minutes === 0) {
    const numMatch = lower.match(/(\d+)/);
    if (numMatch) {
      minutes = parseInt(numMatch[1], 10);
    }
  }

  if (hours === 0 && minutes === 0) return undefined;

  let iso = 'PT';
  if (hours > 0) iso += `${hours}H`;
  if (minutes > 0) iso += `${minutes}M`;

  return iso;
}

/**
 * Generate meta description from recipe data
 * Aims for 150-160 characters
 */
export function generateMetaDescription(
  title: string,
  description?: string,
  prepTime?: string,
  cookTime?: string,
): string {
  // Use provided description if available and appropriate length
  if (description && description.length >= 100 && description.length <= 160) {
    return description;
  }

  // Generate from title and times
  let meta = `Learn how to make ${title}.`;

  if (prepTime && cookTime) {
    meta += ` Ready in ${prepTime} prep + ${cookTime} cook time.`;
  } else if (prepTime || cookTime) {
    meta += ` Ready in ${prepTime || cookTime}.`;
  }

  meta += ' Easy homemade recipe with step-by-step instructions.';

  // Truncate if too long
  if (meta.length > 160) {
    meta = meta.substring(0, 157) + '...';
  }

  return meta;
}

/**
 * Site-wide SEO constants
 */
export const SEO_CONSTANTS = {
  siteName: "Evan's Oven",
  siteUrl: 'https://evans-oven.com',
  defaultAuthor: 'Evan',
  defaultImage: 'https://evans-oven.com/og-image.jpg', // TODO: Create OG image
  twitterHandle: '@evansoven', // TODO: Update if you have Twitter
  locale: 'en_US',
};

/**
 * Get the URL path for a recipe
 * Uses slug if available, otherwise falls back to ID
 */
export function getRecipeUrl(recipe: { id: string; slug?: string }): string {
  return `/recipe/${recipe.slug || recipe.id}`;
}
